import React from 'react';
import { motion } from 'framer-motion';
import { CheckSquare, Download, Share2, AlertTriangle, ThumbsUp } from 'lucide-react';
import { Link } from 'react-router-dom';

const CarpenterChecklist = () => {
  const questions = [
    {
      question: 'Kya ye board factory-laminated hai ya manually paste karoge?',
      english: 'Is this board factory-laminated or will you paste it manually?',
      goodAnswer: 'Factory-laminated with CriLam PUR-Bond™ technology',
      badAnswer: 'Manual pasting with water-based glue',
      why: 'Factory lamination ensures zero bubbles and permanent bond. Manual pasting leads to edge lifting in 1-2 years.'
    },
    {
      question: 'Edge bonding ki guarantee kitne saal ki hai?',
      english: 'How many years guarantee on edge bonding?',
      goodAnswer: '10 years written guarantee',
      badAnswer: 'No guarantee / 1 year only',
      why: 'CriLam PUR-bonded products come with 10-year guarantee. Manual pasting cannot offer this.'
    },
    {
      question: 'Gas stove ke paas ye laminate uthega toh?',
      english: 'Will this laminate lift near the gas stove?',
      goodAnswer: 'No, PUR is heat resistant up to 150°C',
      badAnswer: 'Laminate quality pe depend karta hai',
      why: 'Water-based glue softens at 60°C. Only PUR lamination survives kitchen heat.'
    },
    {
      question: 'Sink area mein paani se kya hoga?',
      english: 'What happens with water near sink area?',
      goodAnswer: 'PUR is 100% waterproof, no problem',
      badAnswer: 'Thoda dhyan rakhna padega',
      why: 'Water-based glue fails with moisture. PUR creates permanent waterproof bond.'
    },
    {
      question: 'Bubble ya uneven surface aayega toh?',
      english: 'What if bubbles or uneven surface appears?',
      goodAnswer: 'Factory lamination = Zero bubbles guaranteed',
      badAnswer: 'Carefully karenge, nahi aana chahiye',
      why: 'Manual application always has risk of bubbles. Machine precision eliminates this.'
    },
    {
      question: 'Kitne saal tak ye product chalna chahiye?',
      english: 'How many years should this product last?',
      goodAnswer: '15+ years with CriLam PUR-Bond™ technology',
      badAnswer: '5-7 saal toh chalega',
      why: 'Manual pasting fails in 3-5 years. CriLam PUR lasts 15+ years.'
    }
  ];

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'CriLam - Ask Your Carpenter Checklist',
          text: 'Important questions to ask before getting lamination work done. Don\'t let manual pasting ruin your investment!',
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback - copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert('Link copied to clipboard!');
    }
  };

  return (
    <div className="min-h-screen bg-[#F7F8F5]">
      {/* Hero Section */}
      <section className="relative bg-[#0A0D0C] pt-32 pb-20 overflow-hidden">
        <div className="absolute inset-0 noise-overlay pointer-events-none" />
        
        <div className="container mx-auto px-6 lg:px-12 relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl mx-auto text-center"
          >
            <div className="inline-block px-4 py-2 bg-[#D4AF37] text-[#0A0D0C] text-sm font-bold mb-6">
              FREE CHECKLIST
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-6 font-['Outfit']">
              Ask Your Carpenter
            </h1>
            <p className="text-xl text-white/70 mb-4">
              6 Important Questions Before Getting Lamination Work Done
            </p>
            <p className="text-lg text-[#D4AF37]">
              Don't let manual pasting ruin your expensive interiors!
            </p>
          </motion.div>
        </div>
      </section>

      {/* Introduction */}
      <section className="py-12 bg-white border-b">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto">
            <div className="flex items-start gap-4 p-6 bg-yellow-50 border border-yellow-200 rounded-lg">
              <AlertTriangle className="h-6 w-6 text-yellow-600 flex-shrink-0 mt-1" />
              <div>
                <h3 className="font-semibold text-yellow-800 mb-2">Why This Checklist Matters</h3>
                <p className="text-yellow-700 text-sm">
                  Most carpenters use water-based glue for manual pasting. This saves them time and cost, 
                  but your laminate will start lifting within 1-2 years - especially in kitchens and bathrooms. 
                  Ask these questions BEFORE work begins to protect your investment.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Questions */}
      <section className="py-16">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto space-y-6">
            {questions.map((item, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1, duration: 0.5 }}
                className="bg-white border border-gray-200 rounded-xl overflow-hidden"
              >
                {/* Question Header */}
                <div className="p-6 bg-[#004534] text-white">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 bg-[#D4AF37] rounded-full flex items-center justify-center flex-shrink-0">
                      <span className="font-bold text-[#004534]">{index + 1}</span>
                    </div>
                    <div>
                      <h3 className="text-xl font-bold mb-1">"{item.question}"</h3>
                      <p className="text-white/70 text-sm italic">{item.english}</p>
                    </div>
                  </div>
                </div>

                {/* Answers */}
                <div className="p-6 grid md:grid-cols-2 gap-4">
                  {/* Good Answer */}
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <ThumbsUp className="h-5 w-5 text-green-600" />
                      <span className="font-semibold text-green-700">Good Answer ✓</span>
                    </div>
                    <p className="text-green-800 font-medium">{item.goodAnswer}</p>
                  </div>

                  {/* Bad Answer */}
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <div className="flex items-center gap-2 mb-2">
                      <AlertTriangle className="h-5 w-5 text-red-500" />
                      <span className="font-semibold text-red-700">Warning Sign ✗</span>
                    </div>
                    <p className="text-red-800 font-medium">{item.badAnswer}</p>
                  </div>
                </div>

                {/* Why It Matters */}
                <div className="px-6 pb-6">
                  <p className="text-sm text-gray-600">
                    <span className="font-semibold text-[#004534]">Why it matters: </span>
                    {item.why}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Share Section */}
      <section className="py-12 bg-white border-t">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-2xl font-bold text-[#004534] mb-4">Share This Checklist</h3>
            <p className="text-gray-600 mb-6">
              Help your friends and family avoid costly mistakes. Share this checklist before they start their interior work.
            </p>
            <button
              onClick={handleShare}
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#D4AF37] text-[#0A0D0C] font-medium hover:bg-[#004534] hover:text-white transition-colors rounded-lg"
            >
              <Share2 className="h-5 w-5" />
              Share on WhatsApp / Social Media
            </button>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-[#004534]">
        <div className="container mx-auto px-6 lg:px-12">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl font-bold text-white mb-4 font-['Outfit']">
              Want Factory-Laminated Products?
            </h2>
            <p className="text-lg text-white/70 mb-8">
              CriLam Decorative offers PUR-bonded, factory-laminated boards with 10-year guarantee. 
              No manual pasting. No edge lifting. No bubbles.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/pur-technology">
                <button className="px-8 py-4 bg-[#D4AF37] text-[#0A0D0C] font-medium hover:bg-white transition-colors">
                  Learn About CriLam PUR-Bond™ Technology
                </button>
              </Link>
              <Link to="/contact">
                <button className="px-8 py-4 border border-white/30 text-white hover:bg-white/10 transition-colors">
                  Contact Us
                </button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default CarpenterChecklist;
